import { UnreachableError } from 'base/helpers';
import { CardStyle, CardStyleRadiusOption } from 'base/styleguide/model';
import { Button, ButtonPalette, RadioButtonGroupWrapper } from 'base/ui/button';
import { IconChecked, IconShuffleAi, IconVisibleOutline } from 'base/ui/icons';
import { Inline, Rows } from 'base/ui/layout';
import { PanelLayout } from 'base/ui/panel';
import { TextSmall } from 'base/ui/typography';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React from 'react';
import cardStyleThumbnailImage from './card-style-thumbnail.file.webp';
import {
  CARD_STYLES,
  CARD_STYLE_RADIUS_OPTIONS,
  getCardStyleLabel,
  getCardStyleRadiusOptionIcon,
  globalCardStyleOverrideStore,
} from './card_styles';
import styles from './card_styles_panel.css';
import { ConceptAPI, getGlobalConceptsStore } from './concept_store';
import { SidebarNavigation } from './routes';

export const CardStylesPanel = observer(
  ({ navigation }: { navigation: SidebarNavigation }) => {
    const conceptsStore = getGlobalConceptsStore();
    const concept = conceptsStore.activeConcept;

    return (
      <PanelLayout
        title="Cards & Images"
        navigation={navigation}
        header={
          <ButtonPalette>
            <RadioButtonGroupWrapper>
              {CARD_STYLE_RADIUS_OPTIONS.map((option) => (
                <Button
                  key={option}
                  icon={getCardStyleRadiusOptionIcon(option)}
                  variant={
                    concept.cardStyleRadiusOption === option
                      ? 'secondary'
                      : 'subtle'
                  }
                  onClick={() => {
                    concept.setCardStyleRadiusOption(option);
                  }}
                />
              ))}
            </RadioButtonGroupWrapper>
          </ButtonPalette>
        }
        footer={
          <CardsPanelFooter
            onShuffle={() => {
              concept.shuffle({ cardStyle: true, overrideLocked: true });
            }}
          />
        }
      >
        <Rows spacing={12}>
          {CARD_STYLES.map((cardStyle) => (
            <CardStyleCard
              key={cardStyle}
              cardStyle={cardStyle}
              concept={concept}
            />
          ))}
        </Rows>
      </PanelLayout>
    );
  },
);

type CardsPanelFooterProps = { onShuffle: () => void };

const CardsPanelFooter = ({ onShuffle }: CardsPanelFooterProps) => {
  return (
    <Button
      label="Shuffle"
      variant="secondary"
      icon={IconShuffleAi}
      iconColor="ai"
      onClick={onShuffle}
      tooltip="Shuffle UI styling"
      fullWidth={true}
    />
  );
};

const CardStyleCard = observer(
  ({ cardStyle, concept }: { cardStyle: CardStyle; concept: ConceptAPI }) => {
    const isSelected = concept.cardStyle === cardStyle;

    const radiusOption = concept.cardStyleRadiusOption;

    return (
      <div
        className={classNames(styles.card, isSelected && styles.selected)}
        onClick={() => {
          concept.setCardStyle(cardStyle);
        }}
      >
        <Inline spacing={8} xalign="space-between" yalign="center">
          <TextSmall>{getCardStyleLabel(cardStyle)}</TextSmall>
          <Inline spacing={8} yalign="center">
            <div className={styles.previewIcon}>
              <Button
                icon={IconVisibleOutline}
                variant="secondary"
                size="small"
                tooltip="Previewing"
                onMouseEnter={() => {
                  globalCardStyleOverrideStore.set(cardStyle);
                }}
                onMouseLeave={() => {
                  globalCardStyleOverrideStore.set(null);
                }}
              />
            </div>
            {isSelected && (
              <div className={styles.selectedIcon} aria-label="Selected">
                <IconChecked />
              </div>
            )}
          </Inline>
        </Inline>

        <div className={styles.cardContent}>
          <CardStylePreview
            className={styles.preview}
            cardStyle={cardStyle}
            radiusOption={radiusOption}
            foregroundColor="white"
          >
            <div
              className={styles.thumbnail}
              style={{ backgroundImage: `url(${cardStyleThumbnailImage})` }}
            />
          </CardStylePreview>

          <div className={styles.smallPreviews}>
            {new Array(2).fill(0).map((_, idx) => (
              <CardStylePreview
                key={idx}
                className={styles.smallPreview}
                cardStyle={cardStyle}
                radiusOption={radiusOption}
                foregroundColor="white"
              />
            ))}
          </div>
        </div>
      </div>
    );
  },
);

export const CardStylePreview = ({
  className,
  cardStyle,
  radiusOption,
  children,
  foregroundColor,
}: React.PropsWithChildren<{
  className?: string;
  cardStyle: CardStyle;
  radiusOption: CardStyleRadiusOption;
  foregroundColor: 'grey' | 'white';
}>) => {
  let previewRadiusStyle: string;

  switch (radiusOption) {
    case CardStyleRadiusOption.Radius0:
      previewRadiusStyle = styles.previewRadius0;
      break;
    case CardStyleRadiusOption.Radius1:
      previewRadiusStyle = styles.previewRadius1;
      break;
    case CardStyleRadiusOption.Radius2:
      previewRadiusStyle = styles.previewRadius2;
      break;
    case CardStyleRadiusOption.Radius3:
      previewRadiusStyle = styles.previewRadius3;
      break;
    default:
      throw new UnreachableError(radiusOption);
  }

  let previewCardStyle: string;

  switch (cardStyle) {
    case CardStyle.Default:
      previewCardStyle = styles.previewStyleDefault;
      break;
    case CardStyle.Outlined:
      previewCardStyle = styles.previewStyleOutlined;
      break;
    case CardStyle.Flat:
      previewCardStyle =
        foregroundColor === 'white'
          ? styles.previewStyleFlat
          : styles.previewStyleFlatGrey;
      break;
    case CardStyle.Edgy:
      previewCardStyle = styles.previewStyleEdgy;
      break;
    default:
      throw new UnreachableError(cardStyle);
  }

  return (
    <div
      className={classNames(className, previewCardStyle, previewRadiusStyle)}
    >
      {children}
    </div>
  );
};
